import React from 'react';
import { AddSupplierModal, Button, Col, Input, Row, SupplierSelect, Table, Typography } from 'components';
import styles from 'feature/brandTabs/pricingAndSuppliers/styles.module.scss';
import {
  CloseIcon,
  DoneIcon,
  FilterIcon,
  RemoveIcon,
  StarIcon,
  TableEmptyIcon,
  ViewIcon,
} from 'components/common/icons';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { HasAccess } from 'components/common/accessControl';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { useSupplierGrid } from 'feature/brandTabs/pricingAndSuppliers/supplierGrid/useSupplierGrid';
import { SupplierRemoveModel } from 'feature/brandTabs/pricingAndSuppliers/supplierGrid/supplierRemoveModal';
import { MainSupplierSetModal } from 'feature/brandTabs/pricingAndSuppliers/supplierGrid/mainSupplierSetModal';
import { MainSupplierRemoveModal } from 'feature/brandTabs/pricingAndSuppliers/supplierGrid/mainSupplierRemoveModal';
import { Filters } from './filters';

const { Text } = Typography;

export interface BrandSupplierDataType {
  id: string;
  name: string;
  markup: number;
  averageMargin: number;
  averageLeadTime: number;
  dealsCount: number;
  dealsAmount: number;
  priceListUpdated: any;
  starred: boolean;
  setMain: boolean;
}

export const SupplierGrid = React.memo(() => {
  const { hasAccess } = useAccessControl();
  const {
    data,
    isLoading,
    onSearch,
    handleTableChange,
    onMainSetModalStateChange,
    onMainRemoveModalStateChange,
    onSupplierRemoveModalStateChange,
    onAddSupplierModalStateChange,
    pagination,
    total,
    handleSupplierChange,
    onSupplierSelectCancel,
    suppliersToAdd,
    contextHolder,
    onAddSupplier,
    onStarChange,
    isAddVisible,
    mainSupplierSetModalState,
    mainSupplierRemoveModalState,
    supplierRemoveModalState,
    addSupplierModalState,
    supplierId,
    filterDrawerState,
    filtersForm,
    onCloseFilters,
    onOpenFilters,
    applyFilters,
    resetFilters,
    isQueryApplied,
    onRedirectToSupplier,
  } = useSupplierGrid();
  const columns: ColumnsType<BrandSupplierDataType> = [
    {
      title: 'Suppliers',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      minWidth: 110,
    },
    {
      title: 'Margin',
      dataIndex: 'markup',
      key: 'markup',
      sorter: true,
      minWidth: 100,
    },
    {
      title: 'Average Margin',
      dataIndex: 'averageMargin',
      key: 'averageMargin',
      sorter: true,
      minWidth: 150,
      // render: (value) => value?.name,
    },
    {
      title: 'Lead time',
      dataIndex: 'averageLeadTime',
      key: 'averageLeadTime',
      minWidth: 110,
      // render: (value) => value?.day,
      sorter: true,
    },
    {
      title: 'Deals Count',
      dataIndex: 'dealsCount',
      key: 'dealsCount',
      minWidth: 130,
      sorter: true,
    },
    {
      title: 'Deals Amount',
      dataIndex: 'dealsAmount',
      key: 'dealsAmount',
      minWidth: 140,
      sorter: true,
    },
    {
      title: 'Price List Updated',
      dataIndex: 'formattedPriceListUpdatedDate',
      key: 'priceListUpdated',
      minWidth: 170,
      render: (value) => value?.day,
      sorter: true,
    },
    {
      fixed: 'right',
      width: 171,
      key: 'actions',
      render: (value) => {
        return (
          <Row gutter={[8, 0]} wrap={false} align={'middle'} justify={'space-between'}>
            <HasAccess role={PERMISSIONS.brands.main_supplier.write}>
              <Col>
                <Button
                  className={styles.table_action_test}
                  variant={'link'}
                  type={'link'}
                  size={'small'}
                  onClick={() => (value.setMain ? onMainRemoveModalStateChange() : onMainSetModalStateChange(value.id))}
                >
                  {value.setMain ? 'Remove Main' : 'Set as Main'}
                </Button>
              </Col>
            </HasAccess>
            <Col>
              <Row gutter={[8, 0]} align={'middle'} wrap={false}>
                <HasAccess role={PERMISSIONS.brands.supplier.read}>
                  <Col>
                    <Button
                      onClick={() => onRedirectToSupplier(value.id)}
                      style={{ padding: 0, width: 16, height: 16 }}
                      type={'text'}
                      size={'small'}
                      icon={<ViewIcon />}
                    />
                  </Col>
                </HasAccess>
                <HasAccess role={PERMISSIONS.brands.supplier.write}>
                  <Col>
                    <Button
                      onClick={() => onStarChange(value.starred, value.id)}
                      style={{ padding: 0, width: 16, height: 16 }}
                      type={'text'}
                      size={'small'}
                      icon={<StarIcon checked={value.starred} />}
                    />
                  </Col>
                </HasAccess>
                <HasAccess role={PERMISSIONS.brands.supplier.write}>
                  <Col>
                    <Button
                      onClick={() => onSupplierRemoveModalStateChange(value.id)}
                      style={{ padding: 0, width: 16, height: 16 }}
                      type={'text'}
                      size={'small'}
                      icon={<RemoveIcon />}
                    />
                  </Col>
                </HasAccess>
              </Row>
            </Col>
          </Row>
        );
      },
    },
  ];
  return (
    <>
      {contextHolder}
      <Col className={styles.main_container} span={24}>
        <Row>
          <Col span={24}>
            <Row justify={'space-between'} align={'middle'}>
              <Col>
                <Row align={'middle'} className={'gap-8'}>
                  <Col>
                    <Row className={'gap-8'}>
                      <Text className={styles.label}>All Suppliers</Text>
                      <SupplierSelect
                        disabled={!hasAccess(PERMISSIONS.brands.supplier.write)}
                        maxTagCount={'responsive'}
                        value={suppliersToAdd}
                        mode={'multiple'}
                        style={{ width: '240px' }}
                        placeholder={'Add Supplier'}
                        onChange={handleSupplierChange}
                        showAdd={hasAccess(PERMISSIONS.suppliers.supplier.write)}
                        onAdd={onAddSupplierModalStateChange}
                      />
                    </Row>
                  </Col>
                  {isAddVisible && (
                    <Col>
                      <Row className={'gap-4'} justify={'end'}>
                        <Col>
                          <Button
                            color="default"
                            variant="filled"
                            onClick={onSupplierSelectCancel}
                            icon={<CloseIcon />}
                          />
                        </Col>
                        <Col>
                          <Button onClick={onAddSupplier} type={'primary'} icon={<DoneIcon />} />
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col>
                <Row className={styles.actions_container}>
                  <Col style={{ width: '300px' }}>
                    <Input.Search
                      enterButton={false}
                      placeholder={'Search'}
                      onChange={(e) => onSearch(e.target.value)}
                    />
                  </Col>
                  <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={24} style={{ paddingTop: '20px' }}>
            <Table
              tableLayout={'auto'}
              bordered
              scroll={{
                x: true,
              }}
              rowKey="id"
              size={'small'}
              dataSource={data}
              columns={columns}
              loading={{
                indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                spinning: isLoading,
              }}
              locale={
                isLoading
                  ? { emptyText: null } // Hide "No Data" text while loading
                  : isQueryApplied
                    ? {}
                    : {
                        emptyText: (
                          <Row gutter={[0, 8]} align={'middle'} justify={'center'}>
                            <TableEmptyIcon />
                            <Col span={24}>
                              <Text>No Suppliers</Text>
                            </Col>
                            <Text>No suppliers added yet. Please add suppliers from the dropdown list.</Text>
                          </Row>
                        ),
                      }
              }
              onChange={handleTableChange}
              pagination={{
                position: ['bottomCenter'],
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} items`,
                size: 'default',
                current: pagination.page,
                pageSize: pagination.size,
                total: total,
              }}
            />
          </Col>
        </Row>
      </Col>
      <Filters
        form={filtersForm}
        drawerOpen={filterDrawerState}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <MainSupplierSetModal
        modalState={mainSupplierSetModalState}
        onModalStateChange={onMainSetModalStateChange}
        supplierId={supplierId}
      />
      <SupplierRemoveModel
        modalState={supplierRemoveModalState}
        onModalStateChange={onSupplierRemoveModalStateChange}
        supplierId={supplierId}
      />
      <MainSupplierRemoveModal
        modalState={mainSupplierRemoveModalState}
        onModalStateChange={onMainRemoveModalStateChange}
      />
      <AddSupplierModal onModalStateChange={onAddSupplierModalStateChange} modalState={addSupplierModalState} />
    </>
  );
});
